import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { LessonLog, TestRecord } from '@/types';

export interface PreviousLessonPatch {
  lessonScope?: string;
  nextHomework?: string;
  homeworkCheck?: string;
  notes?: string;
  testScopes?: string[];
}

interface PreviousLessonPanelProps {
  studentId: string;
  lessonId?: string;
  onApply: (patch: PreviousLessonPatch) => void;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const parts = value.split('-');
  if (parts.length !== 3) return value;
  return `${parts[0]}.${parts[1]}.${parts[2]}`;
}

export default function PreviousLessonPanel({
  studentId,
  lessonId,
  onApply,
}: PreviousLessonPanelProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [prev, setPrev] = useState<LessonLog | null>(null);
  const [tests, setTests] = useState<TestRecord[]>([]);
  const [appliedKey, setAppliedKey] = useState('');
  const timerRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('lesson_logs')
        .select('*')
        .eq('student_id', studentId)
        .order('lesson_date', { ascending: false })
        .limit(1);
      if (lessonId) query = query.neq('id', lessonId);
      const { data, error: logError } = await query;
      if (logError) throw logError;
      const log = (data as LessonLog[] | null)?.[0] ?? null;
      setPrev(log);
      if (log) {
        const { data: testData, error: testError } = await supabase
          .from('test_records')
          .select('*')
          .eq('lesson_log_id', log.id)
          .order('created_at', { ascending: true });
        if (testError) throw testError;
        setTests((testData as TestRecord[]) ?? []);
      } else {
        setTests([]);
      }
      setLoaded(true);
    } catch {
      setPrev(null);
      setTests([]);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [studentId, lessonId]);

  // 학생이 바뀌면 이전 기록을 다시 불러오도록 초기화해요.
  useEffect(() => {
    setLoaded(false);
    setPrev(null);
    setTests([]);
  }, [studentId, lessonId]);

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded && !loading) load();
  };

  const flash = (key: string) => {
    setAppliedKey(key);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setAppliedKey(''), 1500);
  };

  const apply = (key: string, patch: PreviousLessonPatch) => {
    onApply(patch);
    flash(key);
  };

  const testScopes = tests.map((t) => t.test_scope).filter((s) => s && s.trim());

  return (
    <div className="rounded-xl border border-dashed border-secondary-300 bg-secondary-50/60 p-3.5">
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-secondary-900">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
            <i className="ri-history-line text-sm"></i>
          </span>
          지난 수업일지 불러와 이어쓰기
        </span>
        <i
          className={`text-lg text-secondary-700 ${
            open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
          }`}
        ></i>
      </button>

      {open && (
        <div className="mt-3">
          {loading ? (
            <p className="flex items-center justify-center gap-2 py-6 text-xs text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-base"></i>
              지난 수업일지를 불러오는 중...
            </p>
          ) : !prev ? (
            <p className="rounded-lg border border-background-300 bg-background-50 py-6 text-center text-xs text-foreground-500">
              아직 이전 수업일지가 없어요. 첫 수업일지는 직접 작성해주세요.
            </p>
          ) : (
            <div className="flex flex-col gap-2.5">
              <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-600">
                <span className="flex items-center gap-1 font-semibold text-foreground-800">
                  <i className="ri-calendar-line"></i>
                  {formatDate(prev.lesson_date)}
                </span>
                {prev.is_makeup && (
                  <span className="rounded-full bg-accent-100 px-2 py-0.5 font-medium text-accent-800">
                    보충 수업
                  </span>
                )}
                {prev.is_absent && (
                  <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-medium text-secondary-800">
                    결석
                  </span>
                )}
                <span className="text-foreground-400">직전 수업 기록이에요</span>
              </div>

              <PrevRow
                label="수업 범위"
                icon="ri-book-open-line"
                value={prev.lesson_scope}
                applied={appliedKey === 'scope'}
                onApply={() => apply('scope', { lessonScope: prev.lesson_scope ?? '' })}
                actionLabel="이어쓰기"
              />

              <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="flex items-center gap-1.5 text-xs font-bold text-foreground-700">
                    <i className="ri-file-edit-line text-secondary-700"></i>
                    지난 수업 숙제
                  </p>
                </div>
                <p className="mt-1.5 whitespace-pre-wrap break-words text-xs text-foreground-600 line-clamp-3">
                  {prev.next_homework?.trim() || '기록 없음'}
                </p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      apply('check', { homeworkCheck: prev.next_homework ?? '' })
                    }
                    className={`flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition whitespace-nowrap ${
                      appliedKey === 'check'
                        ? 'border-primary-400 bg-primary-100 text-primary-700'
                        : 'border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    <i className={appliedKey === 'check' ? 'ri-check-line' : 'ri-add-line'}></i>
                    숙제 이행검사에
                  </button>
                  <button
                    type="button"
                    onClick={() => apply('nextHw', { nextHomework: prev.next_homework ?? '' })}
                    className={`flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition whitespace-nowrap ${
                      appliedKey === 'nextHw'
                        ? 'border-primary-400 bg-primary-100 text-primary-700'
                        : 'border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    <i className={appliedKey === 'nextHw' ? 'ri-check-line' : 'ri-add-line'}></i>
                    다음 숙제에
                  </button>
                </div>
              </div>

              <PrevRow
                label="특이사항"
                icon="ri-information-line"
                value={prev.notes}
                applied={appliedKey === 'notes'}
                onApply={() => apply('notes', { notes: prev.notes ?? '' })}
                actionLabel="이어쓰기"
              />

              {testScopes.length > 0 && (
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="flex items-center gap-1.5 text-xs font-bold text-foreground-700">
                      <i className="ri-quill-pen-line text-secondary-700"></i>
                      지난 테스트 범위
                    </p>
                    <button
                      type="button"
                      onClick={() => apply('tests', { testScopes })}
                      className={`flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition whitespace-nowrap ${
                        appliedKey === 'tests'
                          ? 'border-primary-400 bg-primary-100 text-primary-700'
                          : 'border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      <i className={appliedKey === 'tests' ? 'ri-check-line' : 'ri-add-line'}></i>
                      테스트에 추가
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {testScopes.map((scope, index) => (
                      <span
                        key={`${scope}-${index}`}
                        className="rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-600"
                      >
                        {scope}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <p className="flex items-center gap-1 text-xs text-foreground-500">
                <i className="ri-lightbulb-line text-accent-600"></i>
                버튼을 누르면 해당 항목이 아래 입력칸에 채워져요. 직접 고쳐 써도 돼요.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface PrevRowProps {
  label: string;
  icon: string;
  value: string | null | undefined;
  applied: boolean;
  actionLabel: string;
  onApply: () => void;
}

function PrevRow({ label, icon, value, applied, actionLabel, onApply }: PrevRowProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-bold text-foreground-700">
          <i className={`${icon} text-secondary-700`}></i>
          {label}
        </p>
        <button
          type="button"
          onClick={onApply}
          className={`flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition whitespace-nowrap ${
            applied
              ? 'border-primary-400 bg-primary-100 text-primary-700'
              : 'border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100'
          }`}
        >
          <i className={applied ? 'ri-check-line' : 'ri-add-line'}></i>
          {actionLabel}
        </button>
      </div>
      <p className="mt-1.5 whitespace-pre-wrap break-words text-xs text-foreground-600 line-clamp-3">
        {value?.trim() || '기록 없음'}
      </p>
    </div>
  );
}